import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, Injector, NgZone, PLATFORM_ID } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs';
import { WA_GEOLOCATION, WA_GEOLOCATION_SUPPORT } from '@ng-web-apis/geolocation';
import {
  LocationPermissionAlertComponent,
  LocationPermissionAlertData,
} from '../../core/modals/location-permission-alert/location-permission-alert.component';
import { CreateEvidenciaArchivoBody } from '../../core/models/evidencias.model';

// Añadimos altitud al tipo
export type CaptureLocation = Pick<
  CreateEvidenciaArchivoBody,
  'latitud' | 'longitud' | 'direccionCaptura'
> & { altitud: number | null };

export class CaptureLocationUnavailableError extends Error {
  constructor() {
    super('La ubicacion esta apagada o no disponible.');
    this.name = 'CaptureLocationUnavailableError';
  }
}

export const CAPTURE_LOCATION_POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
};

@Injectable({
  providedIn: 'root',
})
export class CaptureLocationService {
  private readonly positionOptions = CAPTURE_LOCATION_POSITION_OPTIONS;
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly injector = inject(Injector);
  private readonly ngZone = inject(NgZone);
  private readonly dialog = inject(MatDialog);
  private dialogRef: MatDialogRef<LocationPermissionAlertComponent, void> | null = null;

  public async getCaptureLocation(): Promise<CaptureLocation> {
    if (!this.isBrowser || !this.getGeolocationSupport()) {
      this.openLocationUnavailableDialog();
      throw new CaptureLocationUnavailableError();
    }

    const geolocation = this.injector.get(WA_GEOLOCATION);

    return new Promise((resolve, reject) => {
      geolocation.getCurrentPosition(
        (position) => {
          this.runInAngularZone(() => {
            resolve(this.createLocation(position));
          });
        },
        () => {
          this.runInAngularZone(() => {
            this.openLocationUnavailableDialog();
            reject(new CaptureLocationUnavailableError());
          });
        },
        this.positionOptions,
      );
    });
  }

  private createLocation(position: GeolocationPosition): CaptureLocation {
    return {
      latitud: position.coords.latitude,
      longitud: position.coords.longitude,
      altitud: position.coords.altitude, // Extraemos la altitud (será null bajo techo)
      direccionCaptura: '',
    };
  }

  private openLocationUnavailableDialog(): void {
    if (!this.isBrowser || this.dialogRef) {
      return;
    }

    const dialogRef = this.dialog.open(LocationPermissionAlertComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      autoFocus: false,
      data: this.buildLocationUnavailableDialogData(),
    });

    this.dialogRef = dialogRef;

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        if (this.dialogRef === dialogRef) {
          this.dialogRef = null;
        }
      });
  }

  private buildLocationUnavailableDialogData(): LocationPermissionAlertData {
    return {
      title: 'Permiso de ubicación requerido',
      message: 'Activa el permiso de ubicación para continuar.',
      actionText: 'Entendido',
      icon: 'location_off',
      iconBackground: '#fdecea',
      iconColor: '#c62828',
    };
  }

  private getGeolocationSupport(): boolean {
    return this.injector.get(WA_GEOLOCATION_SUPPORT);
  }

  private runInAngularZone<T>(callback: () => T): T {
    if (NgZone.isInAngularZone()) {
      return callback();
    }
    return this.ngZone.run(callback);
  }
}
